import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies in cross-origin requests
});

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const shouldRetryDocumentLock = (error: any): boolean => {
  const status = error?.response?.status;
  if (status !== 409) return false;

  const method = (error?.config?.method || '').toLowerCase();
  if (!method || method === 'get') return false;

  const url: string = error?.config?.url || '';
  // Only auto-retry for document mutation endpoints.
  if (!url.includes('/categories') && !url.includes('/content')) return false;

  const msg: string =
    (typeof error?.response?.data === 'string' && error.response.data) ||
    error?.response?.data?.message ||
    '';
  return msg.toLowerCase().includes('document is currently being saved');
};

// Add response interceptor to handle token validation + document-lock retries
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token is invalid or expired
      localStorage.removeItem('token');
      // Force a hard redirect to ensure the app state is cleared
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
      return Promise.reject(error);
    }

    if (shouldRetryDocumentLock(error)) {
      const config = error.config;
      const currentRetryCount = (config.__documentLockRetryCount || 0) as number;
      const maxRetries = 12; // ~ up to a few seconds worst-case

      if (currentRetryCount < maxRetries) {
        config.__documentLockRetryCount = currentRetryCount + 1;
        // Exponential backoff with cap.
        const delayMs = Math.min(2000, 150 * Math.pow(1.4, currentRetryCount));
        await sleep(delayMs);
        return api.request(config);
      }
    }
    return Promise.reject(error);
  }
);

// Add request interceptor to include token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Default instance uses application/json; FormData must use multipart with a boundary (set by the browser).
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default api; 